import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import samhealthLogo from '../../assets/samhealth.png';

// Styles matching exact layout from image
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontFamily: 'Helvetica',
    fontSize: 12,
    lineHeight: 1.3,
  },
  
  // Header with logos
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 15,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#000000',
  },
  
  logoBox: {
    width: 100,
    height: 56,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
    overflow: 'hidden',
  },
  
  logoText: {
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Blue title bar
  titleBar: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 4,
    marginBottom: 4,
  fontSize: 15,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  
  // Section headers (blue, unified)
  sectionHeader: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 4,
    marginBottom: 2,
    fontSize: 12,
    fontWeight: 'bold',
  },
  
  // Two column layout
  twoColumn: {
    flexDirection: 'row',
    marginBottom: 3,
  },
  
  leftColumn: {
    width: '48%',
    marginRight: '2%',
  },
  
  rightColumn: {
    width: '50%',
  },
  
  // Field rows - compact
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 0.5,
    fontSize: 11,
  alignItems: 'flex-start',
  },
  
  fieldLabel: {
    width: '45%',
    fontSize: 11,
    color: '#000',
  flexWrap: 'wrap',
  },
  
  fieldValue: {
    width: '55%',
    fontSize: 11,
  fontWeight: 'bold',
  flexWrap: 'wrap',
  },
  
  // Hospital logo placeholder
  hospitalLogo: {
    width: 50,
    height: 30,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  },
  
  // Blue section headers
  blueSectionHeader: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 4,
    marginBottom: 2,
    fontSize: 12,
    fontWeight: 'bold',
  },
  
  // Content box
  contentBox: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#000000',
    padding: 5,
    marginBottom: 5,
  },
  
  // Instructions section
  instructionsHeader: {
    backgroundColor: '#1e3a8a',
    color: 'white',
    padding: 5,
    marginBottom: 3,
    fontSize: 11,
    fontWeight: 'bold',
  },
  
  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 15,
    right: 15,
    textAlign: 'center',
    fontSize: 8,
  },
});

const ComprehensiveKYPDocument = ({ application, kypData, leadGeneratorData }) => {
  // Helper function to check if a value is meaningful (not empty, null, undefined, or just whitespace)
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '' && value !== '--' && value !== 'N/A';
    if (typeof value === 'number') return !isNaN(value) && value !== 0;
    if (typeof value === 'boolean') return true; // booleans are always meaningful
    return Boolean(value);
  };

  // Helper function to get the best available value from multiple sources
  const getBestValue = (...sources) => {
    for (const source of sources) {
      if (hasValue(source)) return source;
    }
    return null;
  };

  // Helper function to conditionally render a field row only if it has meaningful data
  const renderFieldRow = (label, ...valueSources) => {
    const value = getBestValue(...valueSources);
    if (!hasValue(value)) return null;
    
    return (
      <View style={styles.fieldRow}>
        <Text style={styles.fieldLabel}>{label}:</Text>
        <Text style={styles.fieldValue}>{value}</Text>
      </View>
    );
  };

  // Helper function to format amounts, returns null if no meaningful amount
  const formatAmountIfExists = (...amounts) => {
    const amount = getBestValue(...amounts);
    if (!hasValue(amount)) return null;
    return formatAmount(amount);
  };

  // Helper function to format dates, returns null if no meaningful date
  const formatDateIfExists = (...dates) => {
    const date = getBestValue(...dates);
    if (!hasValue(date)) return null;
    return formatDate(date);
  };
  // (debug logs removed to prevent noise in production)
  
  // Normalize and dedupe repeated strings like "SelfSelf" or "Raksha HealthCareRaksha HealthCare"
  const normalizeText = (str) => {
    if (typeof str !== 'string') return str;
    let s = str.replace(/\s+/g, ' ').trim();
    // If the entire string is an exact repetition (e.g., "abcabc" => "abc", or repeated multiple times)
    const fullRep = s.match(/^(.+?)\1+$/);
    if (fullRep) s = fullRep[1].trim();
    // Dedupe hyphen or pipe separated sequences
    if (/[\-|–|—]/.test(s)) {
      const parts = s.split(/\s*[\-|–|—]\s*/);
      const dedup = [];
      for (const p of parts) {
        if (dedup.length === 0 || dedup[dedup.length - 1].toLowerCase() !== p.toLowerCase()) dedup.push(p);
      }
      s = dedup.join(' - ');
    }
    return s;
  };

  // Helper function to safely render any value as string
  const safeRender = (value, fallback = '--') => {
    if (value === null || value === undefined || value === '') return fallback;
    
    if (typeof value === 'object') {
      // Handle specific medical history structure: {description: "dhdhdg", diagnosis: "gdgdgdg", duration: "2 years"}
      if (value.description !== undefined && value.diagnosis !== undefined && value.duration !== undefined) {
        const parts = [];
        if (value.diagnosis) parts.push(`Diagnosis: ${value.diagnosis}`);
        if (value.description) parts.push(`Description: ${value.description}`);
        if (value.duration) parts.push(`Duration: ${value.duration}`);
        return parts.join(' | ');
      }
      
      // Handle treatment objects: {diagnosisName, procedureName, description}
      if (value.diagnosisName && value.procedureName) {
        const parts = [];
        if (value.diagnosisName) parts.push(value.diagnosisName);
        if (value.procedureName) parts.push(value.procedureName);
        if (value.description) parts.push(value.description);
        return parts.join(' - ');
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        return value.map(item => safeRender(item)).filter(Boolean).join(', ');
      }
      
      // For any other object, extract string values
      const stringValues = Object.values(value).filter(v => v && typeof v === 'string');
      if (stringValues.length > 0) {
        return stringValues.join(' ');
      }
      
      return fallback;
    }
    
    return normalizeText(String(value));
  };

  // Helper functions
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB');
  };

  const formatAmount = (amount) => {
    if (!amount) return 'Rs. 0';
    return `Rs. ${Number(amount).toLocaleString('en-IN')}`;
  };

  const formatMedicalHistory = (medicalHistory) => {
    if (!medicalHistory) return '--';
    if (typeof medicalHistory === 'string') return medicalHistory || '--';
    if (typeof medicalHistory === 'object') {
      const parts = [];
      if (medicalHistory.diagnosis) parts.push(`Diagnosis: ${medicalHistory.diagnosis}`);
      if (medicalHistory.description) parts.push(`Description: ${medicalHistory.description}`);
      if (medicalHistory.duration) parts.push(`Duration: ${medicalHistory.duration}`);
      return parts.length > 0 ? parts.join(' | ') : '--';
    }
    return String(medicalHistory) || '--';
  };

  const formatTreatments = (treatments) => {
    if (!treatments || !Array.isArray(treatments)) return '--';
    return treatments.map((treatment, index) => {
      const parts = [];
      if (treatment.diagnosisName) parts.push(treatment.diagnosisName);
      if (treatment.procedureName) parts.push(treatment.procedureName);
      if (treatment.description) parts.push(treatment.description);
      return `${index + 1}. ${parts.join(' - ')}`;
    }).join('; ') || '--';
  };

  // Always pick the fullest available PATIENT name only (never policyholder) in priority order
  const getPatientDisplayName = (app) => {
    const candidates = [
      // explicit patient name from composed KYP data if present
      kypData?.applicationDetails?.patientName,
      // various patient name variants on application
      app?.patient?.fullName,
      app?.patient?.originalName,
      app?.patient?.completeName,
      app?.patient?.displayName,
      app?.patient?.legalName,
      app?.patient?.name,
    ].filter(v => typeof v === 'string' && v.trim().length > 0);

    if (candidates.length === 0) return 'Patient';

    // Choose the longest trimmed candidate (assumed most complete)
    const longest = candidates.reduce((a,b) => (b.trim().length > a.trim().length ? b : a));
    const cleaned = longest.replace(/\s+/g,' ').trim();
    return normalizeText(cleaned) || 'Patient';
  };

  // Build a compact Co-pay/Deductible summary from either top-level or policyDetails structure
  const getCoPayDeductibleSummary = () => {
    const cs = kypData?.coPayStructure || kypData?.policyDetails?.coPayStructure || {};
    const ds = kypData?.deductiblesAndSublimits || kypData?.policyDetails?.deductiblesAndSublimits || {};
    const parts = [];

    const fmt = (val, type) => {
      if (val === undefined || val === null || val === '' || Number.isNaN(Number(val))) return '';
      const n = Number(val);
      if (type && String(type).toLowerCase().includes('percentage')) return `${n}%`;
      return `Rs. ${n.toLocaleString('en-IN')}`;
    };

    // Support both normalized and policyDetails shapes
    const pushIf = (label, value, type) => {
      const fv = fmt(value, type);
      if (fv) parts.push(`${label}: ${fv}`);
    };

    // Generic/normalized
    if (cs.generalCoPay !== undefined) pushIf('General Co-pay', cs.generalCoPay, cs.coPayType);

    // PolicyDetails granular flags
    if (cs.generalCoPayApplicable) pushIf('General Co-pay', cs.generalCoPayValue, cs.generalCoPayType);
    if (cs.parentalCoPayApplicable) pushIf('Parental Co-pay', cs.parentalCoPayValue, cs.parentalCoPayType);
    if (cs.locationBasedCoPayApplicable) {
      if (cs.metroCoPayValue !== undefined) pushIf('Metro Co-pay', cs.metroCoPayValue, cs.metroCoPayType);
      if (cs.nonMetroCoPayValue !== undefined) pushIf('Non-Metro Co-pay', cs.nonMetroCoPayValue, cs.nonMetroCoPayType);
    }
    if (cs.mandatoryCoPayApplicable) pushIf('Mandatory Co-pay', cs.mandatoryCoPayValue, cs.mandatoryCoPayType);
    if (cs.nonNetworkCoPayApplicable) pushIf('Non-Network Co-pay', cs.nonNetworkCoPayValue, cs.nonNetworkCoPayType);
    if (cs.voluntaryCoPayApplicable) pushIf('Voluntary Co-pay', cs.voluntaryCoPayValue, cs.voluntaryCoPayType);
    if (cs.nonIntimationCoPayApplicable) pushIf('Non-intimation Co-pay', cs.nonIntimationCoPayValue, cs.nonIntimationCoPayType);

    // Deductible
    if (ds.compulsoryDeductible !== undefined && Number(ds.compulsoryDeductible) > 0) {
      parts.push(`Deductible: Rs. ${Number(ds.compulsoryDeductible).toLocaleString('en-IN')}`);
    }

    return parts.length ? parts.join(' | ') : '';
  };

  // Compute booleans for conditional sections to avoid passing 0/undefined as children
  const showTreatments = Array.isArray(application?.medical?.treatments) && application.medical.treatments.length > 0;
  const showAuthSummary = Boolean(
    kypData?.medicalHistoryVerified !== undefined ||
    kypData?.documentsAuthentic !== undefined ||
    kypData?.treatmentAppropriate !== undefined ||
    (kypData?.coPayStructure && Object.keys(kypData.coPayStructure).length > 0) ||
    (kypData?.deductiblesAndSublimits && Object.keys(kypData.deductiblesAndSublimits).length > 0) ||
    (typeof application?.medical?.estimatedCost === 'number' && application.medical.estimatedCost > 0) ||
    application?.workflow?.medicalVerification?.status === 'approved' ||
    application?.workflow?.medicalVerification?.kypGenerated === true
  );

  return (
    <Document>
      {/* Page 1 - Main KYP Authorization */}
      <Page size="A4" style={styles.page}>
        {/* Header with Logos */}
        <View style={styles.header}>
          <View style={styles.logoBox}>
            {/* SamHealth left logo */}
            <Image src={samhealthLogo} style={{ width: 96, height: 52 }} />
          </View>
          
          <View style={styles.logoBox}>
            {leadGeneratorData?.documents?.entityLogo?.url ? (
              <Image 
                src={leadGeneratorData.documents.entityLogo.url} 
                style={{ width: 74, height: 44 }} 
              />
            ) : (
              <Text style={[styles.logoText, { fontSize: 9, textAlign: 'center' }]}>
                {leadGeneratorData?.entityName || 'Healthcare Partner'}
              </Text>
            )}
          </View>
        </View>

        {/* Title Bar */}
        <Text style={styles.titleBar}>KNOW YOUR PATIENT (KYP) AUTHORIZATION LETTER</Text>

        {/* Basic Info Row */}
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Subject:</Text>
              <Text style={styles.fieldValue}>KYP Authorization for "{getPatientDisplayName(application)}"</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Dated:</Text>
              <Text style={styles.fieldValue}>{formatDate(new Date())}</Text>
            </View>
            {renderFieldRow("Case ID", application?.caseId)}
          </View>
          
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Valid Till:</Text>
              <Text style={styles.fieldValue}>
                {typeof kypData?.validityPeriod === 'string' && kypData.validityPeriod
                  ? kypData.validityPeriod
                  : formatDate(kypData?.validityPeriod)}
              </Text>
            </View>
            {renderFieldRow("Document Type", application?.treatmentType)}
            {renderFieldRow("Intimation ID", kypData?.applicationDetails?.intimationId)}
          </View>
        </View>

        {/* Patient Details Section */}
        <Text style={styles.sectionHeader}>Patient Details</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Patient Name:</Text>
              <Text style={styles.fieldValue}>{getPatientDisplayName(application)}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Relationship with Primary Beneficiary:</Text>
              <Text style={styles.fieldValue}>{safeRender(application?.policyHolder?.isSelf ? 'Self' : application?.patient?.relationship)}</Text>
            </View>
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Age/Gender:</Text>
              <Text style={styles.fieldValue}>{safeRender(application?.patient?.age)} / {safeRender(application?.patient?.gender)}</Text>
            </View>
          </View>
        </View>

        {/* Hospital Details Section */}
        <Text style={styles.blueSectionHeader}>Hospital Details</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.hospitalLogo}>
              <Text style={styles.logoText}>LOGO</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Hospital Name:</Text>
              <Text style={styles.fieldValue}>{application?.hospitalName || application?.hospital?.name || ''}</Text>
            </View>
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>City:</Text>
              <Text style={styles.fieldValue}>{application?.hospital?.city || application?.leadGenerator?.city || ''}</Text>
            </View>
          </View>
        </View>

        {/* Medical Information Section */}
        <Text style={styles.sectionHeader}>Medical Information</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Diagnosis:</Text>
              <Text style={styles.fieldValue}>
                {safeRender(application?.medical?.diagnosis || application?.medical?.treatments?.[0]?.diagnosisName)}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Procedure Name:</Text>
              <Text style={styles.fieldValue}>
                {safeRender(application?.medical?.procedureName || application?.medical?.treatments?.[0]?.procedureName)}
              </Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Medical History:</Text>
              <Text style={styles.fieldValue}>
                {formatMedicalHistory(application?.medical?.medicalHistory)}
              </Text>
            </View>
          </View>
          <View style={styles.rightColumn}>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Treatment Type:</Text>
              <Text style={styles.fieldValue}>{safeRender(application?.medical?.treatmentType)}</Text>
            </View>
            {(Boolean(application?.treatmentPlanned) || Boolean(application?.medical?.hospitalisationType)) && (
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Hospitalisation:</Text>
                <Text style={styles.fieldValue}>
                  {application?.medical?.hospitalisationType || (application?.treatmentPlanned ? application.treatmentPlanned.charAt(0).toUpperCase() + application.treatmentPlanned.slice(1) : '')}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Treatment Details Section */}
        <View>
          <Text style={styles.sectionHeader}>Treatment Details</Text>
          <View style={styles.contentBox}>
            {/* Admission/Discharge Dates moved under Treatment Details */}
            <View style={styles.twoColumn}>
              <View style={styles.leftColumn}>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Date of Admission:</Text>
                  <Text style={styles.fieldValue}>{formatDate(application?.medical?.admissionDate)}</Text>
                </View>
              </View>
              <View style={styles.rightColumn}>
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Date of Discharge:</Text>
                  <Text style={styles.fieldValue}>{formatDate(application?.medical?.dischargeDate)}</Text>
                </View>
              </View>
            </View>

            {/* Treatments Table (if available) */}
            {showTreatments && (
              <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000' }}>
                {/* Header row */}
                <View style={{ flexDirection: 'row', backgroundColor: '#e5e7eb', borderBottomWidth: 1, borderBottomColor: '#000', borderBottomStyle: 'solid' }}>
                  <Text style={[styles.fieldLabel, { width: '28%', fontWeight: 'bold' }]}>Diagnosis</Text>
                  <Text style={[styles.fieldLabel, { width: '24%', fontWeight: 'bold' }]}>Procedure</Text>
                  <Text style={[styles.fieldLabel, { width: '18%', fontWeight: 'bold' }]}>ICD Code</Text>
                  <Text style={[styles.fieldLabel, { width: '30%', fontWeight: 'bold' }]}>Medical Description</Text>
                </View>
                {/* Data rows */}
                {application.medical.treatments.map((t, idx) => (
                  <View key={idx} style={{ flexDirection: 'row', borderBottomWidth: idx === application.medical.treatments.length - 1 ? 0 : 1, borderBottomColor: '#d1d5db', borderBottomStyle: 'solid' }}>
                    <Text style={[styles.fieldValue, { width: '28%', fontWeight: 'normal' }]}>{safeRender(t?.diagnosisName || application?.medical?.diagnosis)}</Text>
                    <Text style={[styles.fieldValue, { width: '24%', fontWeight: 'normal' }]}>{safeRender(t?.procedureName || application?.medical?.procedureName)}</Text>
                    <Text style={[styles.fieldValue, { width: '18%', fontWeight: 'normal' }]}>{safeRender(t?.icdCode || application?.medical?.icdCode)}</Text>
                    <Text style={[styles.fieldValue, { width: '30%', fontWeight: 'normal' }]}>{safeRender(t?.description || t?.clinicalDescription || t?.notes || application?.medical?.description)}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>

  {/* Policy Information Section */}
  <Text style={styles.blueSectionHeader}>Policy Information</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            {renderFieldRow('Policy type', 
              kypData?.policyDetails?.policyType, 
              application?.policy?.type
            )}
            {renderFieldRow('Insurer', 
              kypData?.policyDetails?.insuranceCompany, 
              application?.policy?.insuranceCompany
            )}
            {renderFieldRow('Policy Number', 
              kypData?.policyDetails?.policyNumber, 
              application?.policy?.number
            )}
            {renderFieldRow('Primary Insured', 
              application?.policyHolder?.name, 
              application?.patient?.name
            )}
            {renderFieldRow('Sum Insured (Base)', 
              formatAmountIfExists(
                kypData?.coverageBreakdown?.baseCoverage,
                kypData?.policyDetails?.baseSumInsured,
                application?.policy?.sumInsured
              )
            )}
            {renderFieldRow('Top-up Sum Insured', 
              formatAmountIfExists(
                kypData?.coverageBreakdown?.topUpCoverage,
                kypData?.policyDetails?.topUpSumInsured,
                application?.policy?.topUpSumInsured
              )
            )}
            {renderFieldRow('Cumulative Bonus', 
              formatAmountIfExists(
                kypData?.coverageBreakdown?.cumulativeBonus,
                kypData?.policyDetails?.cumulativeBonus,
                application?.policy?.cumulativeBonus
              )
            )}
          </View>
          <View style={styles.rightColumn}>
            {renderFieldRow('Corporate Name', 
              kypData?.policyDetails?.corporateName, 
              application?.policy?.corporateName
            )}
            {renderFieldRow('TPA', 
              kypData?.policyDetails?.tpaName, 
              application?.policy?.tpa,
              application?.policy?.tpaName
            )}
            {(() => {
              const policyPeriod = getBestValue(
                (kypData?.policyDetails?.inceptionDate && kypData?.policyDetails?.expiryDate) 
                  ? `${kypData.policyDetails.inceptionDate} to ${kypData.policyDetails.expiryDate}`
                  : null,
                (application?.policy?.startDate && application?.policy?.endDate) 
                  ? `${application.policy.startDate} to ${application.policy.endDate}` 
                  : null
              );
              return renderFieldRow('Policy period', policyPeriod);
            })()}
            {renderFieldRow('Relationship with Patient',
              application?.policyHolder?.isSelf ? 'Self' : null,
              application?.patient?.relationship
            )}
            {renderFieldRow('Employee Status',
              kypData?.policyDetails?.employeeActiveStatus ? 'Active' : null
            )}
            {renderFieldRow('Balance Sum Insured', 
              formatAmountIfExists(
                kypData?.policyDetails?.balanceSumInsured,
                kypData?.coverageBreakdown?.balance,
                application?.policy?.balanceSumInsured
              )
            )}
            {renderFieldRow('Effective Total Coverage', 
              formatAmountIfExists(
                kypData?.policyDetails?.effectiveTotalSumInsured,
                kypData?.coverageBreakdown?.totalAvailable
              )
            )}
          </View>
        </View>

        {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>

      {/* Page 2 - Coverage Information and Authorization Summary */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}>
            <Image src={samhealthLogo} style={{ width: 96, height: 52 }} />
          </View>
          
          <View style={styles.logoBox}>
            {leadGeneratorData?.documents?.entityLogo?.url ? (
              <Image 
                src={leadGeneratorData.documents.entityLogo.url} 
                style={{ width: 74, height: 44 }} 
              />
            ) : (
              <Text style={styles.logoText}>
                {leadGeneratorData?.entityName || application?.leadGenerator?.entityName || 'Ayuvriddhi'}
              </Text>
            )}
          </View>
        </View>

        {/* Coverage Information and Eligibility Section */}
        <Text style={styles.sectionHeader}>Coverage Information and Eligibility</Text>
        <View style={styles.twoColumn}>
          <View style={styles.leftColumn}>
            {renderFieldRow("Sum Insured (Base)",
              formatAmountIfExists(kypData?.coverageBreakdown?.baseCoverage)
            )}
            {renderFieldRow("Top-up Sum Insured",
              formatAmountIfExists(kypData?.coverageBreakdown?.topUpCoverage)
            )}
            {renderFieldRow("Sum Insured Utilised during current year",
              formatAmountIfExists(kypData?.coverageBreakdown?.utilized)
            )}
            {renderFieldRow("Eligible Room", 
              kypData?.roomEligibility?.roomAmountPerDay 
                ? `${formatAmount(kypData.roomEligibility.roomAmountPerDay)} per day`
                : getBestValue([
                    kypData?.applicationDetails?.roomEligibility,
                    "Single Standard AC Room"
                  ])
            )}
            {renderFieldRow("Eligible ICU", 
              kypData?.roomEligibility?.icuAmountPerDay 
                ? `${formatAmount(kypData.roomEligibility.icuAmountPerDay)} per day`
                : kypData?.applicationDetails?.icuAmountPerDay 
                  ? `${formatAmount(kypData.applicationDetails.icuAmountPerDay)} per day`
                  : null
            )}
            {renderFieldRow("Other Deductibles", 
              kypData?.deductiblesAndSublimits?.compulsoryDeductible 
                ? formatAmount(kypData.deductiblesAndSublimits.compulsoryDeductible)
                : null
            )}
          </View>
          <View style={styles.rightColumn}>
            {renderFieldRow("Cumulative Bonus",
              formatAmountIfExists(kypData?.coverageBreakdown?.cumulativeBonus)
            )}
            {renderFieldRow("Effective Sum Insured",
              formatAmountIfExists(kypData?.coverageBreakdown?.effectiveTotalSumInsured)
            )}
            {renderFieldRow("Balance Sum Insured",
              formatAmountIfExists(kypData?.coverageBreakdown?.balanceSumInsured)
            )}
            {getCoPayDeductibleSummary() && renderFieldRow("Co-Pay / Deductible", getCoPayDeductibleSummary())}
            {renderFieldRow("Ailment/procedure Sublimit",
              kypData?.deductiblesAndSublimits?.ailmentProcedureSublimits?.length > 0 
                ? kypData.deductiblesAndSublimits.ailmentProcedureSublimits.map(s => `${s.ailmentProcedure}: ${formatAmount(s.sublimitAmount)}`).join(', ')
                : null
            )}
          </View>
        </View>

        {/* Authorization Summary Section */}
    {showAuthSummary && (
          <View>
            <Text style={styles.blueSectionHeader}>Authorization Summary</Text>
            <View style={styles.contentBox}>
              {(kypData?.medicalHistoryVerified !== undefined || application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Medical History Verified:</Text>
                  <Text style={styles.fieldValue}>
                    {(kypData?.medicalHistoryVerified !== undefined)
                      ? (kypData.medicalHistoryVerified ? 'Yes' : 'No')
                      : (application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) ? 'Yes' : 'No'}
                  </Text>
                </View>
              )}
              {(kypData?.documentsAuthentic !== undefined || application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Documents Authentic:</Text>
                  <Text style={styles.fieldValue}>
                    {(kypData?.documentsAuthentic !== undefined)
                      ? (kypData.documentsAuthentic ? 'Yes' : 'No')
                      : (application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) ? 'Yes' : 'No'}
                  </Text>
                </View>
              )}
              {(kypData?.treatmentAppropriate !== undefined || application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Treatment Appropriate to diagnosis:</Text>
                  <Text style={styles.fieldValue}>
                    {(kypData?.treatmentAppropriate !== undefined)
                      ? (kypData.treatmentAppropriate ? 'Yes' : 'No')
                      : (application?.workflow?.medicalVerification?.status === 'approved' || application?.workflow?.medicalVerification?.kypGenerated) ? 'Yes' : 'No'}
                  </Text>
                </View>
              )}
          {getCoPayDeductibleSummary() ? (
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Co-Pay / Deductible:</Text>
              <Text style={styles.fieldValue}>{getCoPayDeductibleSummary()}</Text>
            </View>
          ) : null}
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Sub-limit:</Text>
            <Text style={styles.fieldValue}>
              {kypData?.deductiblesAndSublimits?.ailmentProcedureSublimits?.length > 0 
                ? kypData.deductiblesAndSublimits.ailmentProcedureSublimits.map(s => `${s.ailmentProcedure}: ${formatAmount(s.sublimitAmount)}`).join(', ')
                : 'Not applicable'}
            </Text>
          </View>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Waiting Periods:</Text>
            <Text style={styles.fieldValue}>
              {(() => {
                const waitingPeriods = kypData?.policyDetails?.waitingPeriods;
                if (waitingPeriods) {
                  const parts = [];
                  if (waitingPeriods.general) parts.push(`General: ${waitingPeriods.general}`);
                  if (waitingPeriods.specific) parts.push(`Specific: ${waitingPeriods.specific}`);
                  if (waitingPeriods.ped) parts.push(`PED: ${waitingPeriods.ped}`);
                  return parts.length > 0 ? `${parts.join(' | ')} verified - Yes` : '30 Days | Specific: 2 Years | PED: 4 Years verified - Yes';
                }
                return '30 Days | Specific: 2 Years | PED: 4 Years verified - Yes';
              })()}
            </Text>
          </View>
      {(typeof application?.medical?.estimatedCost === 'number' && application.medical.estimatedCost > 0) && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Estimated Cost:</Text>
                  <Text style={styles.fieldValue}>{formatAmount(application.medical.estimatedCost)}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Instructions Section */}
        <Text style={styles.instructionsHeader}>Instructions</Text>
        <View style={styles.contentBox}>
          <Text style={{ fontSize: 10, lineHeight: 1.3 }}>
            The case is admissible based on documents shared at initial assessment. Final liability will be determined on receipt of final bill, discharge summary and relevant diagnostic reports. Room category upgrades should be supported by relevant medical reasons. Expenses not payable as per policy terms (e.g., admission fee, documentation, etc, consumables, Non-medical expenses, Instrument and equipment charges.) will not be excluded.
          </Text>
        </View>

        {/* Intimation Details (conditionally) */}
        {kypData?.intimationDetails && (
          <View>
            <Text style={styles.blueSectionHeader}>Intimation Details</Text>
            <View style={styles.contentBox}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Intimation Done:</Text>
                <Text style={styles.fieldValue}>
                  {kypData.intimationDetails.intimationDone === true ? 'Yes' : kypData.intimationDetails.intimationDone === false ? 'No' : '--'}
                </Text>
              </View>
              {kypData.intimationDetails.intimationId && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Intimation ID:</Text>
                  <Text style={styles.fieldValue}>{kypData.intimationDetails.intimationId}</Text>
                </View>
              )}
              {kypData.intimationDetails.intimationSource && (
                <View style={styles.fieldRow}>
                  <Text style={styles.fieldLabel}>Intimation Source:</Text>
                  <Text style={styles.fieldValue}>{kypData.intimationDetails.intimationSource}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Policy Analysis (conditionally) */}
        {kypData?.policyAnalysis && (
          <View>
            <Text style={styles.blueSectionHeader}>Policy Analysis</Text>
            <View style={styles.contentBox}>
              <View style={styles.twoColumn}>
                <View style={styles.leftColumn}>
                  {kypData.policyAnalysis.policyValid !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Policy Valid:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.policyValid ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.coverageActive !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Coverage Active:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.coverageActive ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.beneficiaryEligible !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Beneficiary Eligible:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.beneficiaryEligible ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.treatmentCovered !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Treatment Covered:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.treatmentCovered ? 'Yes' : 'No'}</Text></View>
                  )}
                </View>
                <View style={styles.rightColumn}>
                  {kypData.policyAnalysis.preAuthRequired !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Pre-Auth Required:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.preAuthRequired ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.preAuthObtained !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Pre-Auth Obtained:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.preAuthObtained ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.claimLimitAvailable !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Claim Limit Available:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.claimLimitAvailable ? 'Yes' : 'No'}</Text></View>
                  )}
                  {kypData.policyAnalysis.waitingPeriodCompleted !== undefined && (
                    <View style={styles.fieldRow}><Text style={styles.fieldLabel}>Waiting Period Completed:</Text><Text style={styles.fieldValue}>{kypData.policyAnalysis.waitingPeriodCompleted ? 'Yes' : 'No'}</Text></View>
                  )}
                </View>
              </View>
            </View>
          </View>
        )}

        {/* Claim History (conditionally) */}
        {kypData?.claimHistory && (
          <View>
            <Text style={styles.blueSectionHeader}>Claim History (Current Year)</Text>
            <View style={styles.contentBox}>
              {Array.isArray(kypData.claimHistory.previousClaimsCurrentYear) && kypData.claimHistory.previousClaimsCurrentYear.length > 0 ? (
                kypData.claimHistory.previousClaimsCurrentYear.map((c, idx) => (
                  <Text key={idx} style={{ fontSize: 10, marginBottom: 2 }}>• {c?.date || '—'} | {c?.description || 'Claim'} | Rs. {Number(c?.amount || 0).toLocaleString('en-IN')}</Text>
                ))
              ) : (
                <Text style={{ fontSize: 10 }}>No previous claims</Text>
              )}
              {kypData.claimHistory.totalClaimedCurrentYear && (
                <Text style={{ fontSize: 10, marginTop: 4 }}>Total Claimed (CY): Rs. {Number(kypData.claimHistory.totalClaimedCurrentYear).toLocaleString('en-IN')}</Text>
              )}
            </View>
          </View>
        )}

        {/* Special Remarks Section */}
        <Text style={styles.blueSectionHeader}>Special Remarks</Text>
        <View style={styles.contentBox}>
            <Text style={{ fontSize: 10, lineHeight: 1.3 }}>
              {kypData?.letterRemarks && String(kypData.letterRemarks).trim() ? kypData.letterRemarks : 'No special remarks'}
            </Text>
        </View>

        {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>

      {/* Page 3 - Terms and Conditions */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.logoBox}>
            <Image src={samhealthLogo} style={{ width: 96, height: 52 }} />
          </View>
          
          <View style={styles.logoBox}>
            {leadGeneratorData?.documents?.entityLogo?.url ? (
              <Image 
                src={leadGeneratorData.documents.entityLogo.url} 
                style={{ width: 74, height: 44 }} 
              />
            ) : (
              <Text style={styles.logoText}>
                {leadGeneratorData?.entityName || application?.leadGenerator?.entityName || 'Ayuvriddhi'}
              </Text>
            )}
          </View>
        </View>

        {/* Approval Terms and Conditions */}
  <View style={{ borderWidth: 1, borderStyle: 'solid', borderColor: '#000', padding: 8, marginBottom: 10 }}>
          <Text style={{ fontSize: 11, fontWeight: 'bold', marginBottom: 6 }}>
            Approval terms and Conditions (medical loan against insurance Reimbursement)
          </Text>
          
          <Text style={{ fontSize: 9, lineHeight: 1.4, marginBottom: 8 }}>
            This authorization is issued solely to facilitate a medical loan for eligible patients/customers, based on the insurance eligibility assessed by decoding the health insurance policy to the best possible extent with the support of the insured. It does not, in any manner, guarantee claim settlement by the insurer/TPA.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>1. Policy Governance</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            The loan approval and insurance eligibility assessment are strictly subject to the applicable insurance policy terms, including sub-limits, co-payments, deductibles, and reasonable & customary charge conditions. Any upgrade in room category or deviation from policy norms may result in proportionate deductions.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>2. Documentation Requirement</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            All claims are subject to submission and verification of complete and correct hospitalization and discharge documents.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>3. Validity & Integrity</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            This authorization is valid for {kypData?.validityPeriod || '15 days'} from the date of issue. Any misrepresentation, suppression, or concealment of facts by the insured or hospital shall render this authorization and related assessment null and void.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>4. Surgical Procedures</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            Eligible surgical procedures, as per the respective policy terms, are reimbursable only up to the Reasonable & Customary Charges (R&C) / Usual & Customary Charges (UCR) applicable in the geographical area of treatment. Excess billing beyond such standard charges shall not be payable.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>5. KYP Verification</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            The details/documents furnished during the Know Your Patient (KYP) eligibility check form the basis of this assessment. Any discrepancy or misrepresentation later discovered in discharge summaries, IPD records, or medical documentation shall invalidate the assessed amount.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>6. KYC Compliance</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            For claim payouts exceeding ₹1,00,000, submission of valid KYC documents of the proposer/employee/beneficiary is mandatory.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>7. Final Billing</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            The hospital shall raise the final bill strictly as per its declared tariff, free from inflated or non-permissible charges.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>8. Non-Payable Expenses</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 4 }}>
            Expenses not covered under insurance policies (as per IRDAI's Standard Exclusion List) shall not be reimbursable. This includes but is not limited to:
          </Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6, paddingLeft: 10 }}>
            • Admission/registration/documentation charges{'\n'}
            • Diet charges beyond prescription{'\n'}
            • Cosmetic/aesthetic procedures{'\n'}
            • Assistant surgeon fee not justified{'\n'}
            • Routine physiotherapy, nutritional supplements, energy drinks{'\n'}
            • Personal comfort items (telephone, attendant meals, laundry, TV, toiletries, etc.){'\n'}
            • Non-medical consumables excluded under IRDAI's standard non-payable list.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>9. Escalation of Costs & Length of Stay</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6 }}>
            If hospitalization expenses or length of stay are expected to exceed the assessed amount, the hospital shall provide a formal justification with medical records and revised estimates for re-evaluation. Additional financial assistance is subject to policy terms, medical necessity, and lender's discretion.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>10. Policy Exclusions</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 4 }}>
            Hospitalization expenses shall not be payable for the following (as per IRDAI Standard Exclusions):
          </Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 6, paddingLeft: 10 }}>
            • Convalescence, rest cure, rehabilitation, general debility, or run-down condition{'\n'}
            • Congenital external anomalies (unless specifically covered){'\n'}
            • Infertility, sterilization, contraception, maternity, and newborn care (unless covered){'\n'}
            • Dental treatments (except if requiring hospitalization due to accident){'\n'}
            • Cosmetic/plastic surgery (unless medically necessary){'\n'}
            • Preventive health check-ups, vaccinations (unless mandated){'\n'}
            • Experimental, unproven, or investigational treatments{'\n'}
            • Self-inflicted injuries, attempted suicide, or criminal acts{'\n'}
            • Illness arising from alcohol, drugs, or intoxicants{'\n'}
            • Sexually Transmitted Diseases (STD), HIV/AIDS unless expressly covered{'\n'}
            • War, terrorism, nuclear contamination, or hazardous sports-related injuries.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>11. Modern Treatments – Coverage and Exclusions</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3, marginBottom: 4, paddingLeft: 10 }}>
            • Recognized Modern Treatments: As per IRDAI guidelines (e.g., robotic surgeries, immunotherapy, oral chemotherapy, deep brain stimulation, uterine artery embolization, intra-vitreal injections, etc.), expenses shall be covered under reimbursement claims, but strictly subject to policy-specific sub-limits and caps.{'\n'}
            • Unproven/Experimental Procedures: All unproven, unrecognized, or experimental procedures not approved by IRDAI or relevant Indian medical regulatory authorities are excluded from cover.
          </Text>
          
          <Text style={{ fontSize: 10, fontWeight: 'bold', marginBottom: 3 }}>12. Liability in Case of Claim Rejection</Text>
          <Text style={{ fontSize: 9, lineHeight: 1.3 }}>
            The loan provided is purely a financial facilitation extended on the basis of insurance eligibility assessment. In the event that the insurance claim is rejected, reduced, or disallowed by the insurer/TPA due to false, incomplete, or incorrect information/documents furnished by the insured or the hospital, the entire liability to repay the medical loan rests solely with the insured and/or the hospital, and not with the facilitator/lender.
          </Text>
        </View>

        {/* Footer */}
  <Text style={styles.footer}>This is a system-generated document. For verification, quote Case ID</Text>
      </Page>
    </Document>
  );
};

export default ComprehensiveKYPDocument;
